// src/components/SRVCouncilQueue.jsx
import React, { useEffect, useMemo, useState } from "react";
import api from "../axiosSetup";
import dayjs from "dayjs";
import SRVOfferDetailModal from "../workAssignment/SRVOfferDetailModal";
import CustomNavbar from '../CustomNavbar';

// Lokalisierung: Deutsch (damit "dd" -> So, Mo, Di, Mi, Do, Fr, Sa)
import 'dayjs/locale/de';
import cfg from '../../config';
dayjs.locale('de');

function Confirm({ open, title, text, variant = "warning", onOk, onCancel }) {
  if (!open) return null;
  const bg = variant === "danger" ? "#fee2e2" : variant === "success" ? "#ecfdf5" : "#fff7ed";
  const border = variant === "danger" ? "1px solid #ef444455"
              : variant === "success" ? "1px solid #10b98155"
              : "1px solid #f59e0b55";
  return (
    <div
      className="position-fixed top-0 start-0 w-100 h-100"
      style={{ background: "rgba(0,0,0,0.5)", zIndex: 2147483000 }}
      onClick={(e)=>{ if(e.target===e.currentTarget) onCancel?.(); }}
      role="dialog"
      aria-modal="true"
    >
      <div className="container h-100 d-flex align-items-center justify-content-center">
        <div className="card shadow" style={{ width: "min(520px, 94vw)", borderRadius: 12, overflow: "hidden" }}>
          <div className="p-3" style={{ background: bg, borderBottom: border }}>
            <h6 className="m-0">{title}</h6>
          </div>
          <div className="p-3">
            <p className="mb-3" style={{ whiteSpace: "pre-wrap" }}>{text}</p>
            <div className="d-flex justify-content-end gap-2">
              <button className="btn btn-secondary" onClick={onCancel}>Abbrechen</button>
              <button
                className={`btn ${variant==="danger"?"btn-danger":variant==="success"?"btn-success":"btn-warning"}`}
                onClick={onOk}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** NEU: Reject-Dialog mit Textarea für eine optionale Nachricht */
function RejectDialog({ open, title, contextText, note, setNote, maxLen = 1000, onConfirm, onCancel }) {
  if (!open) return null;
  const remaining = Math.max(0, maxLen - (note?.length || 0));
  return (
    <div
      className="position-fixed top-0 start-0 w-100 h-100"
      style={{ background: "rgba(0,0,0,0.5)", zIndex: 2147483000 }}
      onClick={(e)=>{ if(e.target===e.currentTarget) onCancel?.(); }}
      role="dialog"
      aria-modal="true"
    >
      <div className="container h-100 d-flex align-items-center justify-content-center">
        <div className="card shadow" style={{ width: "min(620px, 96vw)", borderRadius: 12, overflow: "hidden" }}>
          <div className="p-3" style={{ background: "#fee2e2", borderBottom: "1px solid #ef444455" }}>
            <h6 className="m-0">{title}</h6>
          </div>
          <div className="p-3">
            <p className="mb-3" style={{ whiteSpace: "pre-wrap" }}>{contextText}</p>

            <label className="form-label fw-semibold">Begründung (optional)</label>
            <textarea
              className="form-control"
              rows={5}
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, maxLen))}
              placeholder="Kurze Begründung für die Ablehnung (max. 1000 Zeichen)…"
            />
            <div className="d-flex justify-content-between mt-1">
              <small className="text-muted">Diese Nachricht wird an Anbieter & Bieter übermittelt.</small>
              <small className="text-muted">{remaining} Zeichen übrig</small>
            </div>

            <div className="d-flex justify-content-end gap-2 mt-3">
              <button className="btn btn-secondary" onClick={onCancel}>Abbrechen</button>
              <button className="btn btn-danger" onClick={onConfirm}>Ablehnen</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SRVCouncilQueue() {
  const [activeTab, setActiveTab] = useState("queue"); // "queue" | "history"
  const [items, setItems] = useState([]);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [q, setQ] = useState("");               // client-side query
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [banner, setBanner] = useState({ type: null, text: "" });

  // optional: Detail-Modal
  const [openDetail, setOpenDetail] = useState(false);
  const [detailOfferId, setDetailOfferId] = useState(null);

  // NEU: Reject-Dialog State
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectOfferId, setRejectOfferId] = useState(null);
  const [rejectContextText, setRejectContextText] = useState("");
  const [rejectNote, setRejectNote] = useState("");

  const token = localStorage.getItem("authToken");

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/swap-marketplace/council/queue`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(Array.isArray(res.data) ? res.data : []);
      setBanner({ type: null, text: "" });
    } catch (err) {
      setBanner({ type: "error", text: err?.response?.data?.message || "Fehler beim Laden der Freigabe-Liste." });
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await api.get(`/api/swap-marketplace/council/history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHistory(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setBanner({ type: "error", text: err?.response?.data?.message || "Fehler beim Laden der Historie." });
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (activeTab === "history") fetchHistory(); /* eslint-disable-next-line */ }, [activeTab]);

  // Client-side Filter
  const normalizedIncludes = (hay, needle) =>
    (hay || "").toString().toLowerCase().includes((needle || "").toString().toLowerCase());

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    return items.filter(it =>
      normalizedIncludes(it.listing?.assignmentName, q) ||
      normalizedIncludes(it.offeredAssignment?.name, q) ||
      normalizedIncludes(it.owner?.name, q) ||
      normalizedIncludes(it.offerer?.name, q) ||
      normalizedIncludes(it.message, q) ||
      String(it.offerId).includes(q.trim())
    );
  }, [items, q]);

  const act = async (offerId, type, note) => {
    // type: "approve" | "reject"
    setActionLoading(true);
    try {
      const url = type === "approve"
        ? `/api/swap-marketplace/offers/${offerId}/council-approve`
        : `/api/swap-marketplace/offers/${offerId}/council-reject`;

      // NEU: Nachricht bei Ablehnung mitsenden
      const payload = type === "reject" ? { message: (note || "").trim() } : {};

      const res = await api.post(url, payload, { headers: { Authorization: `Bearer ${token}` } });
      setBanner({ type: "success", text: res?.data?.message || "Aktion erfolgreich." });
      // Entferne Eintrag aus der Liste
      setItems(prev => prev.filter(x => x.offerId !== offerId));
    } catch (err) {
      setBanner({ type: "error", text: err?.response?.data?.message || "Aktion fehlgeschlagen." });
    } finally {
      setActionLoading(false);
    }
  };

  // Confirm Dialog State (für Approve)
  const [confirm, setConfirm] = useState({ open: false, variant: "success", title: "", text: "", onOk: null });
  const askApprove = (it) => setConfirm({
    open: true, variant: "success",
    title: "Gegenangebot freigeben?",
    text: `Freigabe für:\n• Angebot: ${it.listing.assignmentName}\n• Gegenangebot: ${it.offeredAssignment.name}\n\nAngebotsersteller: ${it.owner.name}\nGegenangebotsersteller: ${it.offerer.name}`,
    onOk: () => { setConfirm(prev => ({ ...prev, open: false })); act(it.offerId, "approve"); }
  });

  // NEU: eigener Dialog für Reject mit Textarea
  const askReject = (it) => {
    setRejectOfferId(it.offerId);
    setRejectContextText(
      `Ablehnung für:\n• Angebot: ${it.listing.assignmentName}\n• Gegenangebot: ${it.offeredAssignment.name}\n\nAngebotsersteller: ${it.owner.name}\nGegenangebotsersteller: ${it.offerer.name}`
    );
    setRejectNote("");
    setRejectOpen(true);
  };
  const confirmReject = () => {
    const note = rejectNote || "";
    const id = rejectOfferId;
    setRejectOpen(false);
    setRejectOfferId(null);
    setRejectContextText("");
    setRejectNote("");
    act(id, "reject", note);
  };

  // ---------- Hilfsfunktionen & Icons ----------
  const fmtDate = (d) => {
    const dt = dayjs(d);
    return `${dt.format("dd")}, ${dt.format("DD.MM.YYYY")}`;
  };
  const fmtTime = (t) => String(t).slice(0, 5); // HH:mm
  const datesCount = (arr) => Array.isArray(arr) ? arr.length : 0;

  const IconCalendar = ({ className = "me-2", size = 16, color = "#6b7280" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className} aria-hidden="true">
      <rect x="3" y="4" width="18" height="17" rx="2" ry="2" fill="none" stroke={color} strokeWidth="2"/>
      <line x1="16" y1="2.5" x2="16" y2="6" stroke={color} strokeWidth="2" strokeLinecap="round"/>
      <line x1="8"  y1="2.5" x2="8"  y2="6" stroke={color} strokeWidth="2" strokeLinecap="round"/>
      <line x1="3"  y1="9"   x2="21" y2="9" stroke={color} strokeWidth="2"/>
    </svg>
  );

  const IconClock = ({ className = "ms-2 me-1", size = 16, color = "#6b7280" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className} aria-hidden="true">
      <circle cx="12" cy="12" r="9" fill="none" stroke={color} strokeWidth="2"/>
      <line x1="12" y1="7" x2="12" y2="12" stroke={color} strokeWidth="2" strokeLinecap="round"/>
      <line x1="12" y1="12" x2="15" y2="14" stroke={color} strokeWidth="2" strokeLinecap="round"/>
    </svg>
  );

  const SwapBadge = ({ text, variant = "primary" }) => (
    <span className={`badge bg-${variant}`} style={{ fontWeight: 500 }}>{text}</span>
  );

  const Divider = () => (
    <div className="my-3" style={{ height: 1, background: "rgba(0,0,0,0.06)" }} />
  );

  const CardShell = ({ children }) => (
    <div
      className="card border-0 shadow-sm"
      style={{
        borderRadius: 14,
        overflow: "hidden",
        transition: "transform .05s ease, box-shadow .2s ease",
      }}
      onMouseEnter={(e)=>{ e.currentTarget.style.boxShadow="0 .5rem 1rem rgba(0,0,0,.12)"; }}
      onMouseLeave={(e)=>{ e.currentTarget.style.boxShadow="0 .125rem .25rem rgba(0,0,0,.075)"; }}
    >
      {children}
    </div>
  );

  const HeaderRow = ({ it }) => {
    const created = dayjs(it.createdAt);
    const isToday = created.isSame(dayjs(), 'day');
    const isNew = dayjs().diff(created, 'hour') <= 24;
    return (
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 p-3"
           style={{ background: "linear-gradient(180deg, rgba(249,250,251,.9), rgba(255,255,255,1))", borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <SwapBadge text={`${cfg.adminRoleName}: ausstehend`} variant="info" />
          <SwapBadge text={`Offer-ID: ${it.offerId}`} variant="secondary" />
          {isToday && <SwapBadge text="heute" variant="warning" />}
          {isNew && !isToday && <SwapBadge text="neu" variant="success" />}
        </div>
        <div className="text-muted">
          eingegangen: {created.format("DD.MM.YYYY HH:mm")}
        </div>
      </div>
    );
  };

  const SwapHeader = ({ leftName, rightName, leftCount, rightCount }) => (
    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
      <div className="d-flex align-items-center gap-2">
        <span className="fw-semibold">Angebot</span>
        <span className="badge bg-light text-dark">{leftCount} Termin{leftCount===1?"":"e"}</span>
      </div>
      <div className="text-muted" style={{ fontSize: 14 }}>
        wird getauscht gegen
      </div>
      <div className="d-flex align-items-center gap-2">
        <span className="fw-semibold">Gegenangebot</span>
        <span className="badge bg-light text-dark">{rightCount} Termin{rightCount===1?"":"e"}</span>
      </div>

      {/* Namen darunter, prominent */}
      <div className="w-100 d-flex align-items-center justify-content-between mt-1">
        <div className="text-truncate" style={{ maxWidth: "44%" }}>
          <span className="text-muted me-1">•</span>
          <span className="fw-semibold">{leftName}</span>
        </div>
        <div className="text-muted" aria-hidden="true">↔</div>
        <div className="text-truncate text-end" style={{ maxWidth: "44%" }}>
          <span className="fw-semibold">{rightName}</span>
          <span className="text-muted ms-1">•</span>
        </div>
      </div>
    </div>
  );

  // Einsatzname direkt in der Terminliste + Kalender/Uhr-Icons
  const DatesList = ({ title, name, dates }) => (
    <div>
      {/* Titel + Einsatzname + Kalender-Icon */}
      <div className="fw-semibold mb-2 d-flex align-items-center">
        <IconCalendar className="me-2" />
        <span>
          {title}: <span className="text-primary">{name}</span>
        </span>
      </div>

      {Array.isArray(dates) && dates.length > 0 ? (
        <ul className="m-0 ps-0 list-unstyled">
          {dates.map((d, i) => (
            <li key={i} className="mb-2 d-flex align-items-center">
              <span className="me-2 text-muted">•</span>
              <span className="me-2">{fmtDate(d.workDate)}</span>
              <span className="text-muted">—</span>
              <IconClock className="ms-2 me-1" />
              <span className="ms-0">{fmtTime(d.timeFrom)}–{fmtTime(d.timeTo)} Uhr</span>
            </li>
          ))}
        </ul>
      ) : (
        <div className="text-muted">Keine Terminangaben verfügbar.</div>
      )}
    </div>
  );

  const PeopleStrip = ({ owner, offerer }) => (
    <div className="row g-3">
      <div className="col-12 col-md-6">
        <div className="text-uppercase text-muted" style={{ letterSpacing: ".04em", fontSize: 12 }}>Anbietender (Ursprungsangebot)</div>
        <div className="fw-semibold">{owner.name}{typeof owner.age === "number" ? ` (Alter: ${owner.age})` : ""}</div>
      </div>
      <div className="col-12 col-md-6">
        <div className="text-uppercase text-muted" style={{ letterSpacing: ".04em", fontSize: 12 }}>Bieter (Gegenangebot)</div>
        <div className="fw-semibold">{offerer.name}{typeof offerer.age === "number" ? ` (Alter: ${offerer.age})` : ""}</div>
      </div>
    </div>
  );

  const COUNCIL_HISTORY_STATUS = {
    genehmigt: { cls: "bg-success", label: "Freigegeben" },
    abgelehnt: { cls: "bg-danger",  label: "Abgelehnt" },
  };

  return (
    <>
      <CustomNavbar></CustomNavbar>
      <div className="container my-3">

        {/* Banner */}
        {banner.type && (
          <div className={`alert ${banner.type==="error"?"alert-danger":"alert-success"}`} role="alert">
            {banner.text}
          </div>
        )}

        {/* Tab-Leiste */}
        <ul className="nav nav-tabs mb-4">
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "queue" ? "active" : ""}`}
              onClick={() => setActiveTab("queue")}
            >
              Offene Anfragen
              {items.length > 0 && (
                <span className="badge bg-warning text-dark ms-2">{items.length}</span>
              )}
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "history" ? "active" : ""}`}
              onClick={() => setActiveTab("history")}
            >
              Historie
            </button>
          </li>
        </ul>

        {/* ── HISTORY TAB ─────────────────────────────────────────── */}
        {activeTab === "history" && (
          <div>
            <div className="d-flex justify-content-end mb-3">
              <button className="btn btn-outline-secondary btn-sm" onClick={fetchHistory} disabled={historyLoading}>
                {historyLoading ? "Lade…" : "Neu laden"}
              </button>
            </div>
            {historyLoading ? (
              <p>Lade Historie…</p>
            ) : history.length === 0 ? (
              <div className="alert alert-secondary">Keine Einträge in der Historie.</div>
            ) : (
              <div className="row g-3">
                {history.map((it) => {
                  const meta = COUNCIL_HISTORY_STATUS[it.councilStatus] ?? { cls: "bg-secondary", label: it.councilStatus };
                  return (
                    <div key={it.offerId} className="col-12">
                      <div className="card border-0 shadow-sm" style={{ borderRadius: 12 }}>
                        <div className="card-body">
                          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                            <div className="d-flex gap-2 align-items-center">
                              <span className={`badge ${meta.cls}`}>{meta.label}</span>
                              <span className="badge bg-secondary">Offer-ID: {it.offerId}</span>
                            </div>
                            <span className="text-muted" style={{ fontSize: 13 }}>
                              Entschieden: {it.reviewedAt ? dayjs(it.reviewedAt).format("DD.MM.YYYY HH:mm") : "—"}
                            </span>
                          </div>
                          <div className="row g-2">
                            <div className="col-12 col-md-6">
                              <div className="text-muted" style={{ fontSize: 12 }}>Angebot</div>
                              <div className="fw-semibold">{it.listing?.assignmentName ?? "—"}</div>
                              <div className="text-muted" style={{ fontSize: 12 }}>Anbieter: {it.owner?.name ?? "—"}</div>
                            </div>
                            <div className="col-12 col-md-6">
                              <div className="text-muted" style={{ fontSize: 12 }}>Gegenangebot</div>
                              <div className="fw-semibold">{it.offeredAssignment?.name ?? "—"}</div>
                              <div className="text-muted" style={{ fontSize: 12 }}>Bieter: {it.offerer?.name ?? "—"}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── QUEUE TAB ───────────────────────────────────────────── */}
        {activeTab === "queue" && <>
        {/* Client-side Suche */}
        <div className="d-flex flex-column flex-lg-row align-items-lg-end gap-2 mb-3">
          <div className="flex-grow-1">
            <label className="form-label">Suche:</label>
            <input
              className="form-control"
              placeholder="Nach Einsatz, Person, Nachricht oder Offer-ID suchen…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <div>
            <button className="btn btn-primary" onClick={fetchData} disabled={loading}>Neu laden</button>
          </div>
        </div>

        {/* Liste */}
        {loading ? (
          <p>Lade Freigabe-Liste…</p>
        ) : filtered.length === 0 ? (
          <div className="alert alert-secondary">Keine passenden Gegenangebote gefunden.</div>
        ) : (
          <div className="row g-3">
            {filtered.map((it) => {
              const leftDates = it.listing?.dates || [];
              const rightDates = it.offeredAssignment?.dates || [];
              const leftName = it.listing?.assignmentName || "—";
              const rightName = it.offeredAssignment?.name || "—";

              return (
                <div key={it.offerId} className="col-12">
                  <CardShell>
                    <HeaderRow it={it} />

                    <div className="card-body">

                      {/* Swap Kopf: was wird getauscht */}
                      <SwapHeader
                        leftName={leftName}
                        rightName={rightName}
                        leftCount={datesCount(leftDates)}
                        rightCount={datesCount(rightDates)}
                      />

                      <Divider />

                      {/* Termine beider Seiten mit Icons */}
                      <div className="row g-4">
                        <div className="col-12 col-md-6">
                          <div className="d-flex align-items-start gap-2">
                            <div
                              className="rounded-circle flex-shrink-0"
                              style={{ width: 10, height: 10, marginTop: 7, background: "#3b82f6" }}
                              aria-hidden="true"
                            />
                            <DatesList title="Angebot – Termine" name={leftName} dates={leftDates} />
                          </div>
                        </div>
                        <div className="col-12 col-md-6">
                          <div className="d-flex align-items-start gap-2">
                            <div
                              className="rounded-circle flex-shrink-0"
                              style={{ width: 10, height: 10, marginTop: 7, background: "#10b981" }}
                              aria-hidden="true"
                            />
                            <DatesList title="Gegenangebot – Termine" name={rightName} dates={rightDates} />
                          </div>
                        </div>
                      </div>

                      <Divider />

                      {/* Beteiligte */}
                      <PeopleStrip owner={it.owner} offerer={it.offerer} />

                      {/* Nachricht(en) */}
                      {(it.listing?.reason || it.message) && (
                        <>
                          <Divider />
                          <div className="row g-3">
                            {it.listing?.reason && (
                              <div className="col-12 col-lg-6">
                                <div className="fw-semibold mb-1">Nachricht des Ursprungsangebots</div>
                                <div className="border rounded p-2" style={{ whiteSpace: "pre-wrap", background: "#fafafa" }}>
                                  {it.listing.reason}
                                </div>
                              </div>
                            )}
                            {it.message && (
                              <div className="col-12 col-lg-6">
                                <div className="fw-semibold mb-1">Nachricht des Gegenangebots</div>
                                <div className="border rounded p-2" style={{ whiteSpace: "pre-wrap", background: "#fafafa" }}>
                                  {it.message}
                                </div>
                              </div>
                            )}
                          </div>
                        </>
                      )}

                    </div>

                    {/* Footer / Aktionen */}
                    <div className="d-flex flex-wrap gap-2 align-items-center p-3 pt-0">
                      <button
                        className="btn btn-success"
                        disabled={actionLoading}
                        onClick={() => askApprove(it)}
                      >
                        Freigeben
                      </button>
                      <button
                        className="btn btn-danger"
                        disabled={actionLoading}
                        onClick={() => askReject(it)}
                      >
                        Ablehnen
                      </button>
                      <button
                        className="btn btn-outline-secondary ms-auto"
                        onClick={() => { setDetailOfferId(it.offerId); setOpenDetail(true); }}
                      >
                        Details ansehen
                      </button>
                    </div>
                  </CardShell>
                </div>
              );
            })}

            {/* Optionales Detail-Modal */}
            {openDetail && detailOfferId && (
              <SRVOfferDetailModal
                open={openDetail}
                onClose={() => { setOpenDetail(false); setDetailOfferId(null); }}
                offerId={detailOfferId}
                onAfterAction={() => {
                  setOpenDetail(false);
                  setDetailOfferId(null);
                  fetchData(); // nach Entscheidung neu laden
                }}
              />
            )}
          </div>
        )}
        </> /* Ende Queue-Tab */}

        {/* Approve Confirm */}
        <Confirm
          open={confirm.open}
          title={confirm.title}
          text={confirm.text}
          variant={confirm.variant}
          onOk={confirm.onOk}
          onCancel={() => setConfirm(prev => ({ ...prev, open: false }))}
        />

        {/* NEU: Reject-Dialog mit Textarea */}
        <RejectDialog
          open={rejectOpen}
          title="Gegenangebot ablehnen?"
          contextText={rejectContextText}
          note={rejectNote}
          setNote={setRejectNote}
          onConfirm={confirmReject}
          onCancel={() => { setRejectOpen(false); setRejectOfferId(null); setRejectContextText(""); setRejectNote(""); }}
        />
      </div>
    </>
  );
}